/**
 * Speaker enquiry modal.
 *
 * Opens the Gravity Form the hero's Inquire button points at. The form is
 * rendered inline and hidden by the block's render.php rather than fetched on
 * open, so Gravity Forms enqueues its own assets through the normal path and
 * this file only has to manage visibility, focus and dismissal.
 *
 * Enqueued as the speaker-layout block's viewScript, which WordPress loads
 * wherever the block renders — including templates, where has_block() against
 * post_content cannot see it.
 */

const FOCUSABLE = [
	'a[href]',
	'button:not([disabled])',
	'input:not([disabled]):not([type="hidden"])',
	'select:not([disabled])',
	'textarea:not([disabled])',
	'[tabindex]:not([tabindex="-1"])',
].join( ',' );

/**
 * Wire one modal to the buttons that open it.
 *
 * @param {HTMLElement} modal The dialog element.
 */
function initModal( modal ) {
	/*
	 * Two ways in. The hero's button carries the data attribute; anything
	 * linking to the dialog's own id opens it too, which is what lets the FAQ
	 * band's "Request a Call" be a plain core Button the client can edit in the
	 * Site Editor. A core block cannot carry an arbitrary data attribute, but it
	 * can carry an href — and an href to #dn-speaker-inquire is a sensible
	 * fallback on its own if this script never runs.
	 */
	const openers = document.querySelectorAll(
		'[data-dn-inquire-open], a[href="#' + modal.id + '"]'
	);

	if ( ! openers.length ) {
		return;
	}

	let lastFocused = null;

	const isOpen = () => ! modal.hasAttribute( 'hidden' );

	const focusable = () =>
		Array.from( modal.querySelectorAll( FOCUSABLE ) ).filter(
			( el ) => null !== el.offsetParent
		);

	function open() {
		lastFocused = document.activeElement;
		modal.removeAttribute( 'hidden' );
		document.body.classList.add( 'dn-modal-open' );

		const first = focusable()[ 0 ];

		if ( first ) {
			first.focus();
		}
	}

	function close() {
		if ( ! isOpen() ) {
			return;
		}

		modal.setAttribute( 'hidden', '' );
		document.body.classList.remove( 'dn-modal-open' );

		if ( lastFocused && 'function' === typeof lastFocused.focus ) {
			lastFocused.focus();
		}
	}

	openers.forEach( ( button ) => {
		button.addEventListener( 'click', ( event ) => {
			event.preventDefault();
			open();
		} );
	} );

	modal.addEventListener( 'click', ( event ) => {
		if ( event.target.closest( '[data-dn-inquire-close]' ) ) {
			event.preventDefault();
			close();
		}
	} );

	// Escape closes; Tab is trapped, so focus cannot wander to the page behind
	// a dialog that covers it.
	document.addEventListener( 'keydown', ( event ) => {
		if ( ! isOpen() ) {
			return;
		}

		if ( 'Escape' === event.key ) {
			close();
			return;
		}

		if ( 'Tab' !== event.key ) {
			return;
		}

		const items = focusable();

		if ( ! items.length ) {
			return;
		}

		const first = items[ 0 ];
		const last = items[ items.length - 1 ];

		if ( event.shiftKey && document.activeElement === first ) {
			event.preventDefault();
			last.focus();
		} else if ( ! event.shiftKey && document.activeElement === last ) {
			event.preventDefault();
			first.focus();
		}
	} );
}

function init() {
	const modal = document.getElementById( 'dn-speaker-inquire' );

	if ( modal ) {
		initModal( modal );
	}
}

if ( 'loading' === document.readyState ) {
	document.addEventListener( 'DOMContentLoaded', init );
} else {
	init();
}
